import { useEffect } from 'react';

/**
 * Production base URL. Change this here when moving to the final domain
 * (e.g. https://manastir-gradac.rs). Used for canonical links and any
 * absolute-URL meta we set client-side.
 */
export const SITE_URL = 'https://manastir-gradac.fly.dev';

/**
 * Sets `document.title` and updates canonical and description meta tags
 * for the lifetime of the calling component, restoring previous values on unmount.
 *
 * Titles and descriptions are intentionally passed as raw Cyrillic/Latin strings
 * as they are stored in the DB, to provide optimal indexing for search engines.
 */
export function useDocumentTitle(
  title: string,
  canonicalPath?: string,
  description?: string
): void {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    // Canonical URL
    let canonicalEl: HTMLLinkElement | null = null;
    let prevHref: string | null = null;
    if (canonicalPath !== undefined) {
      canonicalEl = document.querySelector('link[rel="canonical"]');
      if (canonicalEl) {
        prevHref = canonicalEl.getAttribute('href');
        canonicalEl.setAttribute('href', SITE_URL + canonicalPath);
      }
    }

    // Meta descriptions (SEO, OpenGraph, Twitter)
    const metaDescEl = document.querySelector('meta[name="description"]');
    const ogDescEl = document.querySelector('meta[property="og:description"]');
    const twitterDescEl = document.querySelector('meta[name="twitter:description"]');

    const prevMetaDesc = metaDescEl?.getAttribute('content') || null;
    const prevOgDesc = ogDescEl?.getAttribute('content') || null;
    const prevTwitterDesc = twitterDescEl?.getAttribute('content') || null;

    if (description) {
      // Clean markdown bold syntax if present
      const cleanDesc = description.replace(/\*\*/g, '').slice(0, 160);
      metaDescEl?.setAttribute('content', cleanDesc);
      ogDescEl?.setAttribute('content', cleanDesc);
      twitterDescEl?.setAttribute('content', cleanDesc);
    }

    return () => {
      document.title = prevTitle;
      if (canonicalEl && prevHref !== null) {
        canonicalEl.setAttribute('href', prevHref);
      }
      if (description) {
        if (prevMetaDesc !== null) metaDescEl?.setAttribute('content', prevMetaDesc);
        if (prevOgDesc !== null) ogDescEl?.setAttribute('content', prevOgDesc);
        if (prevTwitterDesc !== null) twitterDescEl?.setAttribute('content', prevTwitterDesc);
      }
    };
  }, [title, canonicalPath, description]);
}
